import type { ExerciseDef } from './types'

// ─── 8.4 word2vec ─────────────────────────────────────────────────────────────

export const cooccurrence: ExerciseDef = {
  starter: `// "You shall know a word by the company it keeps." Count which words appear near which.
random.seed(1)
const sents = data.sentences(2000).map(s => s.split(" "))
const vocab = [...new Set(sents.flat())].sort()
const id = new Map(vocab.map((w, i) => [w, i]))
console.log(vocab.length, "words, e.g.", sents[0].join(" "))

// 1. counts(window): a vocab × vocab matrix. C[a][b] = how often word b appears within
//    "window" positions of word a (on either side, in the same sentence, not counting a itself).
function counts(window: number): number[][] {
  const C = vocab.map(() => new Array(vocab.length).fill(0))
  return C
}

// 2. similar(C, word, k): the k other words whose rows are most similar (cosine) to this word's row.
function similar(C: number[][], word: string, k: number): string[] {
  return []
}

const C = counts(2)
for (const w of ["cat", "bread", "kitchen", "teacher"]) console.log(w, "→", similar(C, w, 4).join(", "))
`,
  solution: `random.seed(1)
const sents = data.sentences(2000).map(s => s.split(" "))
const vocab = [...new Set(sents.flat())].sort()
const id = new Map(vocab.map((w, i) => [w, i]))
console.log(vocab.length, "words, e.g.", sents[0].join(" "))

function counts(window: number): number[][] {
  const C = vocab.map(() => new Array(vocab.length).fill(0))
  for (const s of sents) {
    for (let i = 0; i < s.length; i++) {
      for (let j = Math.max(0, i - window); j <= Math.min(s.length - 1, i + window); j++) {
        if (j !== i) C[id.get(s[i])!][id.get(s[j])!]++
      }
    }
  }
  return C
}

function similar(C: number[][], word: string, k: number): string[] {
  const row = C[id.get(word)!]
  return vocab
    .filter(w => w !== word)
    .sort((a, b) => vec.cosine(C[id.get(b)!], row) - vec.cosine(C[id.get(a)!], row))
    .slice(0, k)
}

const C = counts(2)
for (const w of ["cat", "bread", "kitchen", "teacher"]) console.log(w, "→", similar(C, w, 4).join(", "))
`,
  tests: `
test("the matrix is vocab × vocab", () => expect(counts(1).length, "rows").toBe(vocab.length))
test("'the' and 'cat' are counted as neighbours", () => expect(counts(2)[id.get("the")][id.get("cat")], "C[the][cat]").toBeGreaterThan(0))
test("counts are symmetric", () => {
  const __C = counts(2)
  expect(__C[id.get("the")][id.get("cat")], "C[the][cat]").toBe(__C[id.get("cat")][id.get("the")])
})
test("similar returns k words", () => expect(similar(counts(2), "cat", 4), "similar(C, 'cat', 4)").toHaveLength(4))
test("'cat' keeps company like the other animals", () => expect(similar(counts(2), "cat", 4).every(w => ["dog", "fox", "horse", "rabbit", "mouse"].includes(w)), "neighbours of cat are animals").toBe(true))
test("'bread' is near other foods", () => expect(similar(counts(2), "bread", 3).every(w => ["apple", "cheese", "cake", "rice", "soup"].includes(w)), "neighbours of bread are foods").toBe(true))
`,
}

export const sgns: ExerciseDef = {
  starter: `// word2vec (skip-gram with negative sampling). Every word gets TWO small vectors:
// "inp" (used when it's the centre word) and "out" (used when it's a neighbour).
// For a real (centre, neighbour) pair, push σ(inp · out) towards 1.
// For a random "negative" word, push σ(inp · out) towards 0.

// update(inp, out, label, lr): one gradient step on the loss −log σ(±inp·out). Change the arrays IN PLACE.
//   g = σ(inp · out) − label               (label is 1 for a real pair, 0 for a negative)
//   inp[k] -= lr · g · out[k]   and   out[k] -= lr · g · inp[k]   (use the OLD inp[k] for the second one!)
function update(inp: number[], out: number[], label: number, lr: number): void {}

random.seed(1)
const sents = data.sentences(2000).map(s => s.split(" "))
const vocab = [...new Set(sents.flat())].sort()
const id = new Map(vocab.map((w, i) => [w, i]))
const DIM = 10
const inp = vocab.map(() => range(DIM).map(() => random.normal(0, 0.1)))
const out = vocab.map(() => new Array(DIM).fill(0))

for (let epoch = 0; epoch < 3; epoch++) {
  for (const s of sents) {
    for (let i = 0; i < s.length; i++) {
      for (let j = Math.max(0, i - 2); j <= Math.min(s.length - 1, i + 2); j++) {
        if (j === i) continue
        const c = id.get(s[i])!
        update(inp[c], out[id.get(s[j])!], 1, 0.05)                    // a real pair
        for (let n = 0; n < 3; n++) update(inp[c], out[random.int(0, vocab.length)], 0, 0.05)   // 3 negatives
      }
    }
  }
}

const nearest = (w: string, k: number) => vocab.filter(x => x !== w).sort((a, b) => vec.cosine(inp[id.get(b)!], inp[id.get(w)!]) - vec.cosine(inp[id.get(a)!], inp[id.get(w)!])).slice(0, k)
for (const w of ["fox", "soup", "river", "girl", "jumps"]) console.log(w, "→", nearest(w, 4).join(", "))
`,
  solution: `function update(inp: number[], out: number[], label: number, lr: number): void {
  const g = 1 / (1 + Math.exp(-vec.dot(inp, out))) - label
  for (let k = 0; k < inp.length; k++) {
    const a = inp[k]
    inp[k] -= lr * g * out[k]
    out[k] -= lr * g * a
  }
}

random.seed(1)
const sents = data.sentences(2000).map(s => s.split(" "))
const vocab = [...new Set(sents.flat())].sort()
const id = new Map(vocab.map((w, i) => [w, i]))
const DIM = 10
const inp = vocab.map(() => range(DIM).map(() => random.normal(0, 0.1)))
const out = vocab.map(() => new Array(DIM).fill(0))

for (let epoch = 0; epoch < 3; epoch++) {
  for (const s of sents) {
    for (let i = 0; i < s.length; i++) {
      for (let j = Math.max(0, i - 2); j <= Math.min(s.length - 1, i + 2); j++) {
        if (j === i) continue
        const c = id.get(s[i])!
        update(inp[c], out[id.get(s[j])!], 1, 0.05)
        for (let n = 0; n < 3; n++) update(inp[c], out[random.int(0, vocab.length)], 0, 0.05)
      }
    }
  }
}

const nearest = (w: string, k: number) => vocab.filter(x => x !== w).sort((a, b) => vec.cosine(inp[id.get(b)!], inp[id.get(w)!]) - vec.cosine(inp[id.get(a)!], inp[id.get(w)!])).slice(0, k)
for (const w of ["fox", "soup", "river", "girl", "jumps"]) console.log(w, "→", nearest(w, 4).join(", "))
`,
  tests: `
test("a real pair is pulled together", () => {
  const __a = [0.1, 0.2], __b = [0.3, -0.1]
  const __before = vec.dot(__a, __b)
  update(__a, __b, 1, 0.5)
  expect(vec.dot(__a, __b), "dot product after").toBeGreaterThan(__before)
})
test("a negative pair is pushed apart", () => {
  const __a = [0.5, 0.5], __b = [0.4, 0.6]
  const __before = vec.dot(__a, __b)
  update(__a, __b, 0, 0.5)
  expect(vec.dot(__a, __b), "dot product after").toBeLessThan(__before)
})
test("uses the old value of inp for out's update", () => {
  const __a = [1], __b = [1]
  update(__a, __b, 0, 1)
  const __g = 1 / (1 + Math.exp(-1))
  expect([__a[0], __b[0]], "[inp, out]").toBeCloseTo([1 - __g, 1 - __g])
})
test("animals end up near animals", () => expect(nearest("fox", 4).every(w => ["cat", "dog", "horse", "rabbit", "mouse"].includes(w)), "neighbours of fox").toBe(true))
test("places end up near places", () => expect(nearest("river", 3).every(w => ["park", "kitchen", "garden", "school", "market"].includes(w)), "neighbours of river").toBe(true))
`,
}

// ─── 8.6 Recurrent networks ───────────────────────────────────────────────────

export const rnnForward: ExerciseDef = {
  starter: `// rnn(xs, Wx, Wh, b): read a sequence of input vectors one at a time.
//   h starts as all zeros (length = b.length).
//   at each step:  h = tanh(x · Wx + h · Wh + b)        (use mat.apply(mat.transpose(W), v) for v · W)
// Return EVERY hidden state, in order.
function rnn(xs: number[][], Wx: number[][], Wh: number[][], b: number[]): number[][] {
  return []
}

const Wx = [[1, 0], [0, 1]]            // 2 inputs → 2 hidden
const Wh = [[0.5, 0], [0, 0.5]]        // the memory: each hidden unit keeps half of its old value
const states = rnn([[1, 0], [0, 0], [0, 0], [0, 1]], Wx, Wh, [0, 0])
console.log(states.map(h => h.map(v => round(v, 3))))   // the first input fades, step by step
`,
  solution: `function rnn(xs: number[][], Wx: number[][], Wh: number[][], b: number[]): number[][] {
  let h = new Array(b.length).fill(0)
  const states: number[][] = []
  for (const x of xs) {
    const fromX = mat.apply(mat.transpose(Wx), x)
    const fromH = mat.apply(mat.transpose(Wh), h)
    h = fromX.map((v, j) => Math.tanh(v + fromH[j] + b[j]))
    states.push(h)
  }
  return states
}

const Wx = [[1, 0], [0, 1]]
const Wh = [[0.5, 0], [0, 0.5]]
const states = rnn([[1, 0], [0, 0], [0, 0], [0, 1]], Wx, Wh, [0, 0])
console.log(states.map(h => h.map(v => round(v, 3))))
`,
  tests: `
test("one hidden state per input", () => expect(rnn([[1, 0], [0, 1], [1, 1]], [[1, 0], [0, 1]], [[0, 0], [0, 0]], [0, 0]), "states").toHaveLength(3))
test("first step is just tanh(x·Wx + b)", () => expect(rnn([[1, 2]], [[1, 0], [0, 1]], [[0, 0], [0, 0]], [0.5, 0])[0], "h1").toBeCloseTo([Math.tanh(1.5), Math.tanh(2)]))
test("the memory carries over", () => {
  const __s = rnn([[1, 0], [0, 0]], [[1, 0], [0, 1]], [[0.5, 0], [0, 0.5]], [0, 0])
  expect(__s[1][0], "h2[0]").toBeCloseTo(Math.tanh(0.5 * Math.tanh(1)))
})
test("Wh mixes hidden units", () => {
  const __s = rnn([[1, 0], [0, 0]], [[1, 0], [0, 1]], [[0, 1], [0, 0]], [0, 0])
  expect(__s[1], "h2").toBeCloseTo([0, Math.tanh(Math.tanh(1))])
})
`,
}

const NAMES_SETUP = `random.seed(42)
const shuffled = random.shuffle(data.names)
const trainNames = shuffled.slice(0, 520), valNames = shuffled.slice(520)
const chars = ".abcdefghijklmnopqrstuvwxyz"
const encode = (names: string[]) => ("." + names.join(".") + ".").split("").map(c => chars.indexOf(c))
const trainStream = encode(trainNames)`

const RNN_MODEL = `random.seed(1)
const H = 24, T = 12
const emb = new Embedding(27, 12)
const Wx = new Linear(12, H)
const Wh = new Linear(H, H, { bias: false })
const head = new Linear(H, 27)
const opt = new Adam([...emb.parameters(), ...Wx.parameters(), ...Wh.parameters(), ...head.parameters()], { lr: 5e-3 })
const step = (chars: number[], h: Tensor) => Wx.forward(emb.forward(chars)).add(Wh.forward(h)).tanh()`

const RNN_LOSS_SOLUTION = `function batchLoss(starts: number[]): Tensor {
  let h = Tensor.zeros(starts.length, H)
  const losses: Tensor[] = []
  for (let t = 0; t < T; t++) {
    h = step(starts.map(p => trainStream[p + t]), h)
    losses.push(head.forward(h).crossEntropy(starts.map(p => trainStream[p + t + 1])))
  }
  return losses.reduce((a, b) => a.add(b)).div(T)
}`

const RNN_TRAIN = `for (let it = 0; it < 300; it++) {
  const starts = range(32).map(() => random.int(0, trainStream.length - T - 1))
  const loss = batchLoss(starts)
  opt.zeroGrad()
  loss.backward()
  opt.step()
}`

const RNN_VAL = `// Average −log P(next char) over the validation names, reading each name from a fresh state.
function rnnValLoss(): number {
  let total = 0, count = 0
  for (const name of valNames) {
    const seq = ("." + name + ".").split("").map(c => chars.indexOf(c))
    let h = Tensor.zeros(1, H)
    for (let i = 0; i + 1 < seq.length; i++) {
      h = step([seq[i]], h)
      total -= Math.log(head.forward(h).softmax().row(0)[seq[i + 1]])
      count++
    }
  }
  return total / count
}`

export const charRnn: ExerciseDef = {
  starter: `${NAMES_SETUP}

${RNN_MODEL}

// batchLoss(starts): unroll the RNN for T steps over a batch of sequences (one per start position).
//   h starts as zeros (batch × H). At each step t:
//     h = step(the characters at positions start + t, h)
//     loss_t = head.forward(h).crossEntropy(the characters at positions start + t + 1)
//   Return the average of the T losses. Backprop through all T steps is automatic!
function batchLoss(starts: number[]): Tensor {
  return Tensor.scalar(0)
}

const chart = plot.live("training loss")
for (let it = 0; it < 300; it++) {
  const starts = range(32).map(() => random.int(0, trainStream.length - T - 1))
  const loss = batchLoss(starts)
  opt.zeroGrad()
  loss.backward()
  opt.step()
  if (it % 10 === 0) chart.push(loss.item())
}

${RNN_VAL}

const valLoss = rnnValLoss()
console.log("validation loss:", round(valLoss, 3), "  (a bigram model scores about 2.54)")
`,
  solution: `${NAMES_SETUP}

${RNN_MODEL}

${RNN_LOSS_SOLUTION}

const chart = plot.live("training loss")
for (let it = 0; it < 300; it++) {
  const starts = range(32).map(() => random.int(0, trainStream.length - T - 1))
  const loss = batchLoss(starts)
  opt.zeroGrad()
  loss.backward()
  opt.step()
  if (it % 10 === 0) chart.push(loss.item())
}

${RNN_VAL}

const valLoss = rnnValLoss()
console.log("validation loss:", round(valLoss, 3), "  (a bigram model scores about 2.54)")
`,
  tests: `
test("batchLoss returns a single number", () => expect(batchLoss([0, 5]).shape, "shape").toEqual([1, 1]))
test("the RNN beats the bigram model on names it hasn't seen", () => expect(valLoss, "validation loss").toBeLessThan(2.5))
`,
}

// ─── 8.8 Project: a name generator ────────────────────────────────────────────

const BIGRAM_SOLUTION = `function bigramValLoss(): number {
  const counts = range(27).map(() => new Array(27).fill(1))   // add-one smoothing
  const s = encode(trainNames)
  for (let i = 0; i + 1 < s.length; i++) counts[s[i]][s[i + 1]]++
  let total = 0, n = 0
  for (const name of valNames) {
    const seq = ("." + name + ".").split("").map(c => chars.indexOf(c))
    for (let i = 0; i + 1 < seq.length; i++) {
      total -= Math.log(counts[seq[i]][seq[i + 1]] / vec.sum(counts[seq[i]]))
      n++
    }
  }
  return total / n
}`

export const nameBaseline: ExerciseDef = {
  starter: `${NAMES_SETUP}
console.log(trainNames.length, "training names,", valNames.length, "validation names, e.g.", valNames.slice(0, 5).join(", "))

// bigramValLoss(): the bigram model's average −log P(next char) on the VALIDATION names.
//  1. Count every (char → next char) pair in the training stream, starting every count at 1
//     ("add-one smoothing", so unseen pairs aren't impossible).
//  2. For each validation name, walk through "." + name + "." and add up −log(count / row total).
//  3. Divide by the number of predictions.
function bigramValLoss(): number {
  return 0
}

console.log("bigram validation loss:", round(bigramValLoss(), 3))
console.log("knowing nothing (27 equally likely chars):", round(Math.log(27), 3))
`,
  solution: `${NAMES_SETUP}
console.log(trainNames.length, "training names,", valNames.length, "validation names, e.g.", valNames.slice(0, 5).join(", "))

${BIGRAM_SOLUTION}

console.log("bigram validation loss:", round(bigramValLoss(), 3))
console.log("knowing nothing (27 equally likely chars):", round(Math.log(27), 3))
`,
  tests: `
test("much better than knowing nothing", () => expect(bigramValLoss(), "bigram loss").toBeLessThan(Math.log(27) - 0.5))
test("about 2.5 nats per character", () => expect(bigramValLoss(), "bigram loss").toBeBetween(2.4, 2.7))
`,
}

export const nameCompare: ExerciseDef = {
  starter: `${NAMES_SETUP}

${BIGRAM_SOLUTION}

// (the RNN from the recurrent networks lesson)
${RNN_MODEL}

${RNN_LOSS_SOLUTION}

${RNN_TRAIN}

${RNN_VAL}

// The MLP from the neural language model lesson: the previous 3 characters → the next one.
random.seed(1)
const mlpEmb = new Embedding(27, 10), mlpHidden = new Linear(30, 32), mlpOut = new Linear(32, 27)
const mlpOpt = new Adam([...mlpEmb.parameters(), ...mlpHidden.parameters(), ...mlpOut.parameters()], { lr: 0.01 })
const mlp = (ctx: number[][]) => mlpOut.forward(mlpHidden.forward(Tensor.concatCols(range(3).map(t => mlpEmb.forward(ctx.map(c => c[t]))))).tanh())
function contexts(names: string[]) {
  const X: number[][] = [], Y: number[] = []
  for (const name of names) {
    let ctx = [0, 0, 0]
    for (const ch of [...name, "."]) {
      X.push(ctx)
      Y.push(chars.indexOf(ch))
      ctx = [...ctx.slice(1), chars.indexOf(ch)]
    }
  }
  return { X, Y }
}
const mlpTrain = contexts(trainNames)
for (let s = 0; s < 1500; s++) {
  const idx = range(32).map(() => random.int(0, mlpTrain.X.length))
  const loss = mlp(idx.map(i => mlpTrain.X[i])).crossEntropy(idx.map(i => mlpTrain.Y[i]))
  mlpOpt.zeroGrad()
  loss.backward()
  mlpOpt.step()
}

// mlpValLoss(): the MLP's average −log P on the validation names.
// Hint: build contexts(valNames) and use .crossEntropy — it already averages.
function mlpValLoss(): number {
  return 0
}

const results = { bigram: bigramValLoss(), mlp: mlpValLoss(), rnn: rnnValLoss() }
console.table(results)
// winner: the name of the model with the LOWEST validation loss
const winner = "bigram"
console.log("best model:", winner)
`,
  solution: `${NAMES_SETUP}

${BIGRAM_SOLUTION}

${RNN_MODEL}

${RNN_LOSS_SOLUTION}

${RNN_TRAIN}

${RNN_VAL}

random.seed(1)
const mlpEmb = new Embedding(27, 10), mlpHidden = new Linear(30, 32), mlpOut = new Linear(32, 27)
const mlpOpt = new Adam([...mlpEmb.parameters(), ...mlpHidden.parameters(), ...mlpOut.parameters()], { lr: 0.01 })
const mlp = (ctx: number[][]) => mlpOut.forward(mlpHidden.forward(Tensor.concatCols(range(3).map(t => mlpEmb.forward(ctx.map(c => c[t]))))).tanh())
function contexts(names: string[]) {
  const X: number[][] = [], Y: number[] = []
  for (const name of names) {
    let ctx = [0, 0, 0]
    for (const ch of [...name, "."]) {
      X.push(ctx)
      Y.push(chars.indexOf(ch))
      ctx = [...ctx.slice(1), chars.indexOf(ch)]
    }
  }
  return { X, Y }
}
const mlpTrain = contexts(trainNames)
for (let s = 0; s < 1500; s++) {
  const idx = range(32).map(() => random.int(0, mlpTrain.X.length))
  const loss = mlp(idx.map(i => mlpTrain.X[i])).crossEntropy(idx.map(i => mlpTrain.Y[i]))
  mlpOpt.zeroGrad()
  loss.backward()
  mlpOpt.step()
}

function mlpValLoss(): number {
  const v = contexts(valNames)
  return mlp(v.X).crossEntropy(v.Y).item()
}

const results = { bigram: bigramValLoss(), mlp: mlpValLoss(), rnn: rnnValLoss() }
console.table(results)
const winner = (Object.keys(results) as (keyof typeof results)[]).reduce((a, b) => (results[b] < results[a] ? b : a))
console.log("best model:", winner)
`,
  tests: `
test("the MLP's validation loss is a sensible number", () => expect(mlpValLoss(), "mlp loss").toBeBetween(2, 2.8))
test("the MLP beats the bigram model", () => expect(results.mlp, "mlp").toBeLessThan(results.bigram))
test("winner is the model with the lowest validation loss", () => expect(results[winner], "winner's loss").toBe(Math.min(results.bigram, results.mlp, results.rnn)))
`,
}

export const nameSample: ExerciseDef = {
  starter: `${NAMES_SETUP}

${RNN_MODEL}

${RNN_LOSS_SOLUTION}

${RNN_TRAIN}

// 1. generate(temperature): start from "." with a zero state; repeatedly step the RNN, turn the scores into
//    probabilities with vec.softmax(scores, temperature), pick with random.sample, stop at "." (or 20 chars).
function generate(temperature: number): string {
  return ""
}

// 2. novelty(samples): the fraction of non-empty samples that are NOT real names in data.names.
function novelty(samples: string[]): number {
  return 0
}

for (const temp of [0.5, 1, 1.5]) {
  const samples = range(30).map(() => generate(temp))
  console.log(\`T = \${temp}: \${samples.slice(0, 8).join(", ")}  … novelty \${round(novelty(samples), 2)}\`)
}
`,
  solution: `${NAMES_SETUP}

${RNN_MODEL}

${RNN_LOSS_SOLUTION}

${RNN_TRAIN}

function generate(temperature: number): string {
  let h = Tensor.zeros(1, H)
  let c = 0
  let out = ""
  for (let i = 0; i < 20; i++) {
    h = step([c], h)
    c = random.sample(vec.softmax(head.forward(h).row(0), temperature))
    if (c === 0) break
    out += chars[c]
  }
  return out
}

function novelty(samples: string[]): number {
  const real = new Set(data.names)
  const nonEmpty = samples.filter(s => s.length > 0)
  return nonEmpty.filter(s => !real.has(s)).length / nonEmpty.length
}

for (const temp of [0.5, 1, 1.5]) {
  const samples = range(30).map(() => generate(temp))
  console.log(\`T = \${temp}: \${samples.slice(0, 8).join(", ")}  … novelty \${round(novelty(samples), 2)}\`)
}
`,
  tests: `
test("generates lowercase names", () => expect(range(10).map(() => generate(1)).every(s => /^[a-z]*$/.test(s)), "all a–z").toBe(true))
test("novelty counts names that aren't in the data", () => expect(novelty(["emma", "zorblax", ""]), "novelty(['emma', 'zorblax', ''])").toBeCloseTo(0.5))
test("at temperature 1 it mostly invents new names", () => expect(novelty(range(40).map(() => generate(1))), "novelty").toBeGreaterThan(0.5))
test("low temperature makes shorter, safer names than high temperature", () => {
  const __len = (t) => vec.mean(range(40).map(() => generate(t).length))
  expect(__len(0.3) < __len(2), "average length at T = 0.3 < at T = 2").toBe(true)
})
`,
}
